import { getResourceData } from '@/api/public';
import { useRouteMetadata } from '@/hooks/useRouteMetadata';
import { getSession, removeSession, setSession } from '@/utils/tools';
import { useParams, useSearchParams } from 'next/navigation';
import { useCallback, useRef } from 'react';
import {
  SiteRedirectData,
  SiteRedirectFromChanged,
  UseSiteRedirectReturn,
} from './types/siteRedirect';
import { safeAsync } from './utils/siteRedirectUtils';
import { useStore } from '@/providers/app-store-provider';
import { useCurrencySettled } from '@/hooks/computed';

/**
 * 单独设置货币时，页面会重新刷新。
 * 因此需要一个session来记录，当设置货币时，设置一个session，页面刷新后，读取并删除这个session。
 */
const FROM_SET_CONF_SESSION_KEY = 'FROM_SET_CONF_SESSION';
const HIDE_SITE_REDIRECT_POPUP_KEY = 'HIDE_SITE_REDIRECT_POPUP';

/**
 * 国家站切换逻辑 Hook
 * 处理国家站切换的弹窗显示、数据获取和跳转逻辑
 */
export const useSiteRedirect = (): UseSiteRedirectReturn => {
  const params = useParams<{ id: string; shop_sku: string }>();
  const route = useRouteMetadata();
  const searchParams = useSearchParams();
  const currencyReady = useCurrencySettled();
  const isInitProcessed = useRef(false);
  const siteRedirectInfo = useStore((state) => state.siteRedirectInfo);
  const setSiteRedirectInfo = useStore((state) => state.setSiteRedirectInfo);

  /**
   * 获取站点切换数据
   */
  const getSiteRedirectData = useCallback(
    (language: string, currency: string, fromChanged?: SiteRedirectFromChanged) => {
      const data: SiteRedirectData = {
        type: undefined, // 2:手动切换，1:自动切换
        language,
        currency,
        page: '',
        parameter: '',
      };
      const fromSet = getSession(FROM_SET_CONF_SESSION_KEY);
      removeSession(FROM_SET_CONF_SESSION_KEY);

      // 设置触发方式
      const fromTrigger: '更改币种语言' | 'IP匹配' | '' =
        fromSet === 'currency' || fromSet === 'language' || fromChanged ? '更改币种语言' : 'IP匹配';

      if (fromSet === 'currency' || fromSet === 'language' || fromChanged) {
        data.type = '2';
      }

      if (route.isHome()) {
        data.page = 'index';
      } else if (route.isGoodsList()) {
        data.page = 'goods-list';
        data.parameter = params.id || '';
      } else if (route.isGoodsDetail()) {
        data.page = 'goods-detail';
        data.parameter = params.shop_sku || '';
      } else {
        data.page = `${route.pathname}?${searchParams.toString()}`;
      }

      return { site_switching: data, fromTrigger };
    },
    [params, route.pathname, searchParams],
  );

  /**
   * 处理站点切换流程
   */
  const handleSiteRedirectProcess = useCallback(
    async ({
      isInit = false,
      fromChanged = '',
      needSessionFlag = false,
      language,
      currency,
    }: {
      isInit?: boolean;
      fromChanged?: SiteRedirectFromChanged;
      needSessionFlag?: boolean;
      language: string;
      currency: string;
    }) => {
      // 初始化时，若币种未准备好，则不执行切换站点流程
      if (isInit) {
        if (!currencyReady) return;
        if (isInitProcessed.current) return;
        isInitProcessed.current = true;
      }

      // 检查是否已隐藏弹窗
      const isHide = getSession(HIDE_SITE_REDIRECT_POPUP_KEY);
      if (isHide) return;

      // 如果需要设置会话标志，则设置后返回
      if (needSessionFlag) {
        setSession(FROM_SET_CONF_SESSION_KEY, fromChanged);
        return;
      }

      await safeAsync(async () => {
        const { site_switching, fromTrigger } = getSiteRedirectData(
          language,
          currency,
          fromChanged,
        );
        const res = await getResourceData({ site_switching });

        if (!res?.data?.site_switching) return;

        const { target_url, pop_msg, pop_status } = res.data.site_switching;

        if (!target_url || !pop_msg) return;

        setSiteRedirectInfo({
          targetUrl: target_url,
          popMsg: pop_msg,
          popStatus: pop_status,
          fromTrigger,
        });
      });
    },
    [currencyReady, getSiteRedirectData],
  );

  /**
   * 处理弹窗显示状态变化
   */
  const onSiteRedirectPopupShow = useCallback((visible: boolean) => {
    if (visible) {
      setSession(HIDE_SITE_REDIRECT_POPUP_KEY, true);
    }
  }, []);

  return {
    siteRedirectInfo,
    getSiteRedirectData,
    handleSiteRedirectProcess,
    onSiteRedirectPopupShow,
  };
};
